import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Settings, X, Check, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { Link } from 'react-router-dom';

const cookieCategories = [
  {
    id: 'necessary',
    name: 'Cookies essentiels',
    description: 'Nécessaires au fonctionnement du site (authentification, sécurité, préférences de session).',
    required: true,
  },
  {
    id: 'analytics',
    name: 'Cookies analytiques',
    description: 'Nous aident à comprendre comment vous utilisez le site pour améliorer votre expérience.',
    required: false,
  },
  {
    id: 'marketing',
    name: 'Cookies marketing',
    description: 'Utilisés pour vous proposer des publicités pertinentes sur d\'autres sites.',
    required: false,
  },
  {
    id: 'preferences',
    name: 'Cookies de préférences',
    description: 'Mémorisent vos paramètres et personnalisations (thème, langue, filtres).',
    required: false,
  },
] as const;

const CookieBanner = () => {
  const { consent, isLoaded, hasConsented, acceptAll, rejectAll, saveConsent } = useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [customConsent, setCustomConsent] = useState({
    analytics: false,
    marketing: false,
    preferences: false,
  });

  // Don't render until we've checked localStorage
  if (!isLoaded || hasConsented()) {
    return null;
  }

  const handleCustomSave = () => {
    saveConsent({
      necessary: true,
      ...customConsent,
      consentDate: new Date().toISOString(),
    });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      >
        <div className="max-w-4xl mx-auto">
          <div className="bg-card/95 backdrop-blur-xl border border-border rounded-2xl shadow-2xl overflow-hidden">
            {/* Main banner */}
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Cookie className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-lg font-bold text-foreground mb-2">
                    Nous respectons votre vie privée
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Nous utilisons des cookies pour améliorer votre expérience sur Kourse. 
                    Vous pouvez personnaliser vos préférences ou accepter notre utilisation par défaut.{' '}
                    <Link to="/cookies" className="text-primary hover:underline">
                      En savoir plus
                    </Link>
                  </p>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  onClick={acceptAll}
                  className="flex-1 gap-2"
                >
                  <Check className="w-4 h-4" />
                  Tout accepter
                </Button>
                <Button
                  onClick={() => setShowDetails(!showDetails)}
                  variant="outline"
                  className="flex-1 gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Personnaliser
                </Button>
                <Button
                  onClick={rejectAll}
                  variant="ghost"
                  className="flex-1 gap-2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                  Refuser
                </Button>
              </div>
            </div>

            {/* Detailed settings */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 border-t border-border pt-4 space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Shield className="w-5 h-5 text-primary" />
                      <span className="font-semibold text-sm">Gérer vos préférences</span>
                    </div>

                    {cookieCategories.map((category) => (
                      <div
                        key={category.id}
                        className="flex items-start justify-between gap-4 p-4 rounded-lg bg-muted/50"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">{category.name}</span>
                            {category.required && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                                Requis
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {category.description}
                          </p>
                        </div>
                        <Switch
                          checked={category.required ? true : customConsent[category.id as keyof typeof customConsent]}
                          onCheckedChange={(checked) => {
                            if (!category.required) {
                              setCustomConsent((prev) => ({
                                ...prev,
                                [category.id]: checked,
                              }));
                            }
                          }}
                          disabled={category.required}
                          className="flex-shrink-0"
                        />
                      </div>
                    ))}

                    <Button
                      onClick={handleCustomSave}
                      className="w-full mt-4"
                    >
                      Enregistrer mes préférences
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieBanner;
